import { ChangeDetectionStrategy, Component, computed, input, output } from '@angular/core';
import {
  InputDirective,
  SelectContentComponent,
  SelectDirective,
  SelectItemDirective,
  SelectPortalDirective,
  SelectTriggerComponent,
  SelectValueDirective,
} from '@/app/shared/ui/spartan';
import { cn, formatDateISO, today } from '@/app/shared/utils';
import {
  clampEndDate,
  clampNotFutureDate,
  clampStartDate,
  computePresetRange,
  DATE_RANGE_PRESET_LABEL,
  DATE_RANGE_PRESET_OPTIONS,
  earlierDate,
  maxEndDateAttr,
  minStartDateAttr,
} from './date-range-selector.logic';
import { DateRangePreset, DateRangeValue } from './date-range-selector.model';

@Component({
  selector: 'app-date-range-selector',
  standalone: true,
  imports: [
    InputDirective,
    SelectDirective,
    SelectTriggerComponent,
    SelectValueDirective,
    SelectPortalDirective,
    SelectContentComponent,
    SelectItemDirective,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './date-range-selector.component.html',
})
export class DateRangeSelectorComponent {
  readonly value = input.required<DateRangeValue>();
  readonly disabled = input(false);

  // eslint-disable-next-line @angular-eslint/no-input-rename -- lets callers write class="..." directly, merged via cn()
  readonly userClass = input<string>('', { alias: 'class' });
  readonly selectContainerClass = input('');
  readonly dateContainerClass = input('');

  readonly valueChange = output<DateRangeValue>();

  protected readonly presetOptions = DATE_RANGE_PRESET_OPTIONS;
  protected readonly presetLabel = DATE_RANGE_PRESET_LABEL;
  protected readonly presetItemToString = (preset: DateRangePreset): string =>
    DATE_RANGE_PRESET_LABEL[preset];

  private readonly todayISO = formatDateISO(today());

  protected readonly preset = computed(() => this.value().preset);
  protected readonly startDate = computed(() => this.value().startDate);
  protected readonly endDate = computed(() => this.value().endDate);

  protected readonly startDateMax = computed(() => earlierDate(this.endDate(), this.todayISO));
  protected readonly startDateMin = computed(() => minStartDateAttr(this.endDate()));
  protected readonly endDateMin = computed(() => this.startDate());
  protected readonly endDateMax = computed(() =>
    earlierDate(maxEndDateAttr(this.startDate()), this.todayISO),
  );

  protected readonly containerComputedClass = computed(() =>
    cn('grid grid-cols-4 items-center gap-2', this.userClass()),
  );

  protected readonly selectContainerComputedClass = computed(() =>
    cn('min-w-44 col-span-1', this.selectContainerClass()),
  );
  protected readonly dateContainerComputedClass = computed(() =>
    cn('items-center flex col-span-1 gap-1', this.dateContainerClass()),
  );

  protected onPresetChange(preset: DateRangePreset): void {
    const range = computePresetRange(preset);
    this.valueChange.emit({
      preset,
      startDate: range?.startDate ?? null,
      endDate: range?.endDate ?? null,
    });
  }

  protected onStartDateChange(rawValue: string): void {
    const startDate = clampNotFutureDate(rawValue || null);
    this.valueChange.emit({
      preset: 'CUSTOM',
      startDate: clampStartDate(startDate, this.endDate()),
      endDate: this.endDate(),
    });
  }

  protected onEndDateChange(rawValue: string): void {
    const endDate = clampNotFutureDate(rawValue || null);
    this.valueChange.emit({
      preset: 'CUSTOM',
      startDate: this.startDate(),
      endDate: clampEndDate(this.startDate(), endDate),
    });
  }
}
